import useQueryParams from '@common/src/hooks/useQueryParams';
import { AnvaFlowsInsuranceTag } from '@flows/src/api/anva-flows/getAnvaFlowsInsuranceTags';
import {
  AnvaProduct,
  useAnvaProducts,
} from '@flows/src/api/anva-flows/getAnvaProducts';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/flowsConfigureStore';
import { PartyType } from '@parties/src/types';
import QuestionViewSkeleton from '@sales-conversation/src/components/skeletons/QuestionViewSkeleton';
import useStartAnvaFlow, {
  AnvaFlowProduct,
} from '@sales-conversation/src/hooks/anva/useStartAnvaFlow';
import { FC, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';

interface getProductsFromConfigProps {
  catalogue: AnvaProduct[];
  insuranceGroups: AnvaFlowsInsuranceTag[];
  companies: Record<string, string[]>;
  coverages: Record<string, string[]>;
}

const getProductsFromConfig = (
  params: getProductsFromConfigProps,
): AnvaFlowProduct[] => {
  const { catalogue } = params;
  const {
    insuranceGroups: chosenInsuranceGroups,
    companies,
    coverages,
  } = params;

  // Iterate through the product catalogue to filter and map products
  return catalogue.reduce((acc, product) => {
    const { insurance, insurance_company } = product;

    // Find the insurance group that matches the current product's insurance ID
    const foundGroup = chosenInsuranceGroups.find(({ insurance_ids }) =>
      insurance_ids.includes(Number(insurance.id)),
    );

    // Create a flow product object with the product ID and associated coverages
    const flowProduct = {
      id: product.id,
      coverages: foundGroup?.id ? coverages[foundGroup?.id] : [],
    };

    // Add the product to the result if it belongs to the selected insurance group
    // and the insurance company is included in the selected companies
    if (
      foundGroup &&
      companies[foundGroup.id]?.includes(insurance_company.id)
    ) {
      acc.push(flowProduct);
    }

    return acc;
  }, [] as AnvaFlowProduct[]);
};

const FlowsConfigureFinish: FC = () => {
  const queryParams = useQueryParams();
  const partyType = (queryParams.get('type') as PartyType) || 'CUSTOMER';
  const { data: catalogue, isSuccess } = useAnvaProducts(partyType);
  const startAnvaFlow = useStartAnvaFlow();
  const { flowId } = useParams<{ flowId: string }>();
  const history = useHistory();

  useEffect(() => {
    // If the required data is not available, exit early
    if (!catalogue || !flowId) return;

    // Get the current state from the flows configure store
    const state = useFlowsConfigureStore.getState();

    // If no insurances are selected, redirect the user back to the configuration page
    if (!state.insurances)
      return history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}`,
        search: window.location.search,
      });

    // Extract the list of products from the catalogue
    const { items } = catalogue;

    // Generate the list of products based on the selected insurances, companies, and coverages
    const products = getProductsFromConfig({
      catalogue: items,
      insuranceGroups: state.insurances,
      companies: state.companies,
      coverages: state.coverages,
    });

    // Start the ANVA flow with the generated products and other required parameters
    startAnvaFlow({
      flowId: flowId,
      inputUrl: `${FLOWS_CONFIG_PATH}/${flowId}`,
      partyId: queryParams.get('partyId') || undefined,
      anva_products: products,
    });
  }, [isSuccess]);

  return <QuestionViewSkeleton />;
};

export default FlowsConfigureFinish;
